/**
 * Reusable concept autocomplete with pagination (load next/prev).
 * Used by Markets, ProductTypes, HA, Tags in FilterBar.
 *
 * Options are domain-scoped (API `domain` header). Reload when the store
 * domain changes so the first open after a switch is not stale.
 */
import { useState, useCallback, useEffect, useRef } from "react";
import { apiClient } from "../../../../services/apiClient";
import { getConceptValues, type ConceptValue } from "../../../../services/regulationsService";
import { selectDomain, useAppStore } from "../../../../stores";

export type ConceptName = "Country" | "ProductType" | "HA" | "Tags";

export interface UseConceptAutocompleteResult {
  input: string;
  setInput: (v: string) => void;
  options: ConceptValue[];
  setOptions: React.Dispatch<React.SetStateAction<ConceptValue[]>>;
  loading: boolean;
  page: number;
  hasMoreNext: boolean;
  loadingMore: "next" | "prev" | null;
  loadOptions: (searchName?: string) => Promise<void>;
  loadNext: () => Promise<void>;
  loadPrev: () => Promise<void>;
}

const DEFAULT_PAGE_SIZE = 20;

export function useConceptAutocomplete(
  conceptName: ConceptName,
  pageSize: number = DEFAULT_PAGE_SIZE,
): UseConceptAutocompleteResult {
  const domain = useAppStore(selectDomain);
  const [input, setInputState] = useState("");
  const inputRef = useRef(input);
  inputRef.current = input;
  const setInput = useCallback((v: string) => {
    inputRef.current = v;
    setInputState(v);
  }, []);
  const [options, setOptions] = useState<ConceptValue[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMoreNext, setHasMoreNext] = useState(true);
  const [loadingMore, setLoadingMore] = useState<"next" | "prev" | null>(null);
  const loadOptionsRequestIdRef = useRef(0);

  const loadOptions = useCallback(
    async (searchName?: string) => {
      const requestId = ++loadOptionsRequestIdRef.current;
      try {
        setLoading(true);
        setPage(1);
        setHasMoreNext(true);
        const trimmedSearch = searchName?.trim() || undefined;
        const response = await getConceptValues(conceptName, trimmedSearch, {
          page: 1,
          page_size: pageSize,
        });
        if (requestId !== loadOptionsRequestIdRef.current) return;
        if (response.code === 200 && response.data) {
          const data = Array.isArray(response.data) ? response.data : [];
          setOptions(data);
          setHasMoreNext(data.length >= pageSize);
        } else {
          setOptions([]);
        }
      } catch (error) {
        if (requestId !== loadOptionsRequestIdRef.current) return;
        console.error(`Error loading ${conceptName}:`, error);
        setOptions([]);
      } finally {
        if (requestId === loadOptionsRequestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [conceptName, pageSize],
  );

  const loadNext = useCallback(async () => {
    if (!hasMoreNext || loadingMore) return;
    const nextPage = page + 1;
    const searchName = inputRef.current.trim() || undefined;
    try {
      setLoadingMore("next");
      const response = await getConceptValues(conceptName, searchName, {
        page: nextPage,
        page_size: pageSize,
      });
      if (response.code === 200 && response.data) {
        const newData = Array.isArray(response.data) ? response.data : [];
        setOptions((prev) => {
          const existingCodes = new Set(prev.map((p) => p.code));
          const appended = newData.filter((d) => !existingCodes.has(d.code));
          return appended.length > 0 ? [...prev, ...appended] : prev;
        });
        setPage(nextPage);
        setHasMoreNext(newData.length >= pageSize);
      } else {
        setHasMoreNext(false);
      }
    } catch {
      setHasMoreNext(false);
    } finally {
      setLoadingMore(null);
    }
  }, [page, hasMoreNext, loadingMore, conceptName, pageSize]);

  const loadPrev = useCallback(async () => {
    if (page <= 1 || loadingMore) return;
    const prevPage = page - 1;
    const searchName = inputRef.current.trim() || undefined;
    try {
      setLoadingMore("prev");
      const response = await getConceptValues(conceptName, searchName, {
        page: prevPage,
        page_size: pageSize,
      });
      if (response.code === 200 && response.data) {
        const newData = Array.isArray(response.data) ? response.data : [];
        setOptions((prev) => {
          const existingCodes = new Set(prev.map((p) => p.code));
          const toPrepend = newData.filter((d) => !existingCodes.has(d.code));
          return toPrepend.length > 0 ? [...toPrepend, ...prev] : prev;
        });
        setPage(prevPage);
      }
    } catch {
      // keep current page
    } finally {
      setLoadingMore(null);
    }
  }, [page, loadingMore, conceptName, pageSize]);

  useEffect(() => {
    // Child effects can run before HomePage syncs the API domain header — set it
    // here so the reload always uses the current store domain.
    if (domain) {
      apiClient.setDomain(domain);
    }
    // Clear immediately so the dropdown never briefly shows the previous domain.
    setOptions([]);
    setPage(1);
    setHasMoreNext(true);
    void loadOptions(inputRef.current.trim() || undefined);
  }, [loadOptions, domain]);

  return {
    input,
    setInput,
    options,
    setOptions,
    loading,
    page,
    hasMoreNext,
    loadingMore,
    loadOptions,
    loadNext,
    loadPrev,
  };
}
